/**
 * Current User Profile Composable
 *
 * This composable manages loading the current user's ClientUser profile
 * and initializing user-specific settings like dark mode preference.
 */

import { ref, computed, watch, readonly } from 'vue'
import { useAuthStore } from '@/stores/auth-store'
import { useUserStore } from '@/stores/user-store'
import { useCurrentClient } from '@/composables/useCurrentClient'
import { useMsal } from '@/azure/useMsal'
import axiosUtil from '@/services/axios-helper'
import { ClientUser } from '@/interfaces/client-user'
import { useColors, useToast } from 'vuestic-ui'
import { useI18n } from 'vue-i18n'
import { getClientRoles } from '@/azure/token-utils'

// Shared state across all composable instances
const isLoading = ref(false)
const error = ref<string | null>(null)
const isDarkModeLoading = ref(false)
const isDarkMode = ref(false)
let isInitialized = false

export const useCurrentUserProfile = () => {
  const authStore = useAuthStore()
  const userStore = useUserStore()
  const { currentClientCode } = useCurrentClient()
  const { instance } = useMsal()
  const { applyPreset } = useColors()
  const { init: notify } = useToast()
  const { t } = useI18n()

  // Get current user's email from auth
  const currentUserEmail = computed(() => {
    return authStore.activeAccount?.username || null
  })

  // Get user's accessible client codes from token claims
  const userClientCodes = computed(() => {
    if (!authStore.idTokenClaims) return []
    const clientRoles = getClientRoles(authStore.idTokenClaims)
    return Object.keys(clientRoles)
  })

  // Get the primary client code for loading user profile
  const primaryClientCode = computed(() => {
    // Prefer current client context if available
    if (currentClientCode.value) {
      return currentClientCode.value
    }
    // Otherwise, use the first client the user has access to
    return userClientCodes.value[0] || null
  })

  // Get the current user's dark mode preference from their profile
  const currentUserDarkMode = computed(() => {
    return userStore.clientUser?.settings?.theme?.darkMode ?? false
  })

  // Apply dark mode to the UI
  const applyDarkMode = (darkMode: boolean) => {
    if (darkMode) {
      document.documentElement.classList.add('dark', 'user-dark-mode')
      applyPreset('dark')
    } else {
      document.documentElement.classList.remove('dark', 'user-dark-mode')
      applyPreset('light')
    }
  }

  // Initialize dark mode from user settings
  const initializeDarkMode = () => {
    const userPreference = userStore.clientUser?.settings?.theme?.darkMode ?? false
    isDarkMode.value = userPreference
    applyDarkMode(userPreference)
  }

  // Load the current user's profile
  const loadCurrentUserProfile = async () => {
    if (!currentUserEmail.value) {
      console.log('No user email available for profile loading')
      return
    }

    if (!primaryClientCode.value) {
      console.log('No client code available for user, user may not have access to any clients')
      return
    }

    try {
      isLoading.value = true
      error.value = null

      console.log(`Loading user profile for ${currentUserEmail.value} in client ${primaryClientCode.value}`)

      // Call API to get current user's profile
      const response = await (
        await axiosUtil(instance)
      ).get('/api/client-users', {
        params: {
          clientCode: primaryClientCode.value,
          onlyYourself: true,
        },
      })

      console.log('API response status:', response.status)
      console.log('API response data:', response.data)

      const clientUsers: ClientUser[] = response.data

      if (clientUsers.length > 0) {
        // Should only return current user's profile
        const currentUser = clientUsers[0]
        console.log('✅ Found current user profile:', currentUser)
        userStore.setClientUser(currentUser)
        // Initialize dark mode after setting user profile
        initializeDarkMode()
      } else {
        console.log('❌ Current user profile not found')

        // User doesn't have a ClientUser record yet - this is expected for new users
        // We'll create one when they first toggle dark mode
        console.log('User will need a ClientUser profile created on first dark mode toggle')

        // Create a temporary local profile for UI purposes only
        const tempProfile: ClientUser = new ClientUser()
        tempProfile.ssoEmail = currentUserEmail.value
        tempProfile.clientCode = primaryClientCode.value
        tempProfile.settings = {
          theme: {
            darkMode: false,
          },
        }

        userStore.setClientUser(tempProfile)
        initializeDarkMode()
      }
    } catch (err) {
      console.error('Failed to load current user profile:', err)
      error.value = 'Failed to load user profile'
      // Clear the client user on error
      userStore.setClientUser(null)
    } finally {
      isLoading.value = false
    }
  }

  // Handle authentication state and load user profile
  const handleAuthentication = async () => {
    if (!authStore.isAuthenticated) {
      console.log('User not authenticated, clearing profile')
      userStore.setClientUser(null)
      return
    }

    console.log('User authenticated, loading profile...')
    console.log('Auth store activeAccount:', authStore.activeAccount)
    console.log('Current user email:', currentUserEmail.value)
    console.log('User client codes:', userClientCodes.value)
    console.log('Primary client code:', primaryClientCode.value)
    console.log('Current userStore.clientUser:', userStore.clientUser)

    // Load the user profile if we don't have it and user has client access
    if (!userStore.clientUser && primaryClientCode.value) {
      await loadCurrentUserProfile()
    }
  }

  // Toggle dark mode and save to user profile
  const toggleDarkMode = async () => {
    console.log('[useCurrentUserProfile] toggleDarkMode called')
    console.log('[useCurrentUserProfile] Current isDarkMode.value:', isDarkMode.value)
    console.log('[useCurrentUserProfile] userStore.clientUser:', userStore.clientUser)

    if (!userStore.clientUser) {
      console.error('Cannot toggle dark mode: User profile not available')
      notify({
        message: 'User profile not available. Please try again.',
        color: 'danger',
      })
      return
    }

    const newDarkMode = !isDarkMode.value
    console.log('[useCurrentUserProfile] Toggling to:', newDarkMode)

    // Update local state first for immediate UI feedback
    isDarkMode.value = newDarkMode
    applyDarkMode(newDarkMode)

    // Save to API
    try {
      isDarkModeLoading.value = true

      // Prepare the updated user settings
      const updatedSettings = {
        ...userStore.clientUser.settings,
        theme: {
          ...userStore.clientUser.settings?.theme,
          darkMode: newDarkMode,
        },
      }

      console.log('Updating user profile with settings:', updatedSettings)

      let response

      // Check if this is a new profile (no id) or existing profile
      if (userStore.clientUser.id) {
        // Update existing profile
        response = await (
          await axiosUtil(instance)
        ).patch(
          `/api/client-users/${userStore.clientUser.id}`,
          {
            _etag: userStore.clientUser._etag,
            clientCode: userStore.clientUser.clientCode, // Include clientCode in body
            settings: updatedSettings,
          },
          {
            params: {
              clientCode: userStore.clientUser.clientCode,
            },
          },
        )
      } else {
        // Create new profile
        console.log('Creating new user profile...')
        response = await (
          await axiosUtil(instance)
        ).post(
          '/api/client-users',
          {
            ssoEmail: userStore.clientUser.ssoEmail,
            clientCode: userStore.clientUser.clientCode,
            settings: updatedSettings,
            status: 'active',
          },
          {
            params: {
              clientCode: userStore.clientUser.clientCode,
            },
          },
        )
      }

      console.log('API response:', response.data)

      // Update the user store with the response data
      userStore.setClientUser(response.data)

      notify({
        message: t('success.success'),
        color: 'success',
      })
    } catch (error) {
      console.error('Failed to update dark mode preference:', error)

      // Revert UI state on error
      isDarkMode.value = !isDarkMode.value
      applyDarkMode(isDarkMode.value)

      notify({
        message: 'Failed to save dark mode preference',
        color: 'danger',
      })
    } finally {
      isDarkModeLoading.value = false
    }
  }

  // Initialize user profile loading
  const initializeUserProfile = () => {
    // Only initialize once
    if (isInitialized) {
      console.log('[useCurrentUserProfile] Already initialized, skipping')
      return
    }
    isInitialized = true
    console.log('[useCurrentUserProfile] Initializing user profile...')

    // Watch for authentication status and load profile when user becomes authenticated
    watch(
      () => authStore.isAuthenticated,
      async (isAuth) => {
        if (isAuth && currentUserEmail.value) {
          await handleAuthentication()
        } else {
          // User logged out, clear profile
          userStore.setClientUser(null)
        }
      },
      { immediate: true },
    )

    // Watch for changes in token claims (when user gets client access)
    watch(
      () => userClientCodes.value,
      async (clientCodes) => {
        if (authStore.isAuthenticated && currentUserEmail.value && clientCodes.length > 0 && !userStore.clientUser) {
          await loadCurrentUserProfile()
        }
      },
    )

    // Watch for changes in user profile and sync the dark mode
    watch(
      () => currentUserDarkMode.value,
      (newValue) => {
        console.log('[useCurrentUserProfile] currentUserDarkMode watcher fired:', {
          newValue,
          currentIsDarkMode: isDarkMode.value,
          willUpdate: newValue !== isDarkMode.value,
        })
        if (newValue !== isDarkMode.value) {
          console.log('[useCurrentUserProfile] Updating isDarkMode from', isDarkMode.value, 'to', newValue)
          isDarkMode.value = newValue
          applyDarkMode(newValue)
        }
      },
      { immediate: true },
    )

    // Load profile immediately if we have the necessary info
    if (authStore.isAuthenticated && currentUserEmail.value) {
      handleAuthentication()
    }
  }

  return {
    isLoading: readonly(isLoading),
    error: readonly(error),
    loadCurrentUserProfile,
    initializeUserProfile,
    // Dark mode functionality
    isDarkMode: readonly(isDarkMode),
    isDarkModeLoading: readonly(isDarkModeLoading),
    toggleDarkMode,
    initializeDarkMode,
  }
}
